import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, FileCheck, FileText, Download, Mail, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReportPreviewDialog from '@/components/reports/ReportPreviewDialog';
import ChangePackageModal from './ChangePackageModal';

const CHECK_NAMES = {
  'address_check': 'Address',
  'criminal_court_check': 'Criminal Court',
  'criminal_advocate_check': 'Criminal Advocate',
  'id_check': 'ID Check',
  'employment_check': 'Employment',
  'education_check': 'Education',
  'credit_check': 'Credit',
  'reference_check': 'Reference',
  'personal_reference': 'Personal Ref',
  'professional_reference': 'Professional Ref',
  'world_criminal_db': 'World Criminal',
  'india_criminal_db': 'India Criminal',
  'global_database': 'Global Database',
  'drug_test': 'Drug Test'
};

export default function CaseDetailView({ caseData, employee, onBack }) {
  const [changePkgOpen, setChangePkgOpen] = useState(false);
  if (!caseData) return null;

  const handleDownloadReport = async () => {
    try {
      toast.info('Generating BGV Report...');
      const caseId = caseData?.id || caseData?.case_id;
      const response = await axios.post(`/cases/${caseId}/generate-bgv-report`, {
        is_final: false
      }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `BGV_Report_${caseId}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const getStatusBadgeClass = (status, overallResult) => {
    if (status === 'completed' || status === 'verified') {
      if (overallResult) {
        if (['discrepancy', 'major_discrepancy', 'red'].includes(overallResult)) return 'bg-red-100 text-red-700';
        if (overallResult === 'minor_discrepancy') return 'bg-rose-100 text-rose-600';
        if (['unable_to_verify', 'amber'].includes(overallResult)) return 'bg-amber-100 text-amber-700';
      }
      return 'bg-emerald-100 text-emerald-700';
    }
    if (status === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (status === 'failed') return 'bg-rose-100 text-rose-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Button variant="outline" size="sm" onClick={onBack}>
          ← Back to List
        </Button>
        <h1 className="text-2xl font-bold text-slate-900">Case Details</h1>
        <Badge variant="outline" className="ml-2">{caseData?.case_id || caseData?.id}</Badge>
        <div className="ml-auto flex items-center gap-2">
          <ReportPreviewDialog 
            caseId={caseData?.id || caseData?.case_id} 
            caseStatus={caseData?.status}
          />
          <Button variant="outline" size="sm" onClick={() => setChangePkgOpen(true)} data-testid="case-detail-change-package-btn">
            <RefreshCw className="w-4 h-4 mr-1" />Change Package
          </Button>
          <Button variant="outline" size="sm" data-testid="generate-bgv-report-btn" onClick={handleDownloadReport}>
            <Download className="w-4 h-4 mr-1" />Download Report
          </Button>
        </div>
      </div>

      <ChangePackageModal
        open={changePkgOpen}
        onOpenChange={setChangePkgOpen}
        caseId={caseData?.id || caseData?.case_id}
        currentPackageId={caseData?.package_id}
        onChanged={() => { setChangePkgOpen(false); if (onBack) onBack(); }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Candidate Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <InfoItem label="Full Name" value={employee?.name} />
                <InfoItem label="Email" value={employee?.email} />
                <InfoItem label="Phone" value={employee?.phone} />
                <InfoItem label="Date of Birth" value={employee?.date_of_birth} />
                <InfoItem label="Father's Name" value={employee?.father_name} />
                <InfoItem label="Gender" value={employee?.gender} />
              </div>

              {(employee?.current_address || employee?.permanent_address) && (
                <div className="mt-6 pt-6 border-t">
                  <p className="font-medium mb-3">Address Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    {employee?.current_address && (
                      <AddressCard title="Current Address" address={employee.current_address} />
                    )}
                    {employee?.permanent_address && (
                      <AddressCard title="Permanent Address" address={employee.permanent_address} />
                    )}
                  </div>
                </div>
              )}

              {(employee?.pan_number || employee?.aadhar_number || employee?.passport_number) && (
                <div className="mt-6 pt-6 border-t">
                  <p className="font-medium mb-3">Identity Documents</p>
                  <div className="grid grid-cols-3 gap-4">
                    {employee?.pan_number && <DocCard label="PAN" value={employee.pan_number} />}
                    {employee?.aadhar_number && <DocCard label="Aadhar" value={employee.aadhar_number} />}
                    {employee?.passport_number && <DocCard label="Passport" value={employee.passport_number} />}
                  </div>
                </div>
              )}

              {(employee?.employment_history?.length > 0 || employee?.employment_entries?.length > 0) && (
                <div className="mt-6 pt-6 border-t">
                  <p className="font-medium mb-3">Employment History</p>
                  <div className="space-y-3">
                    {(employee.employment_entries || employee.employment_history || []).map((emp, idx) => (
                      <div key={idx} className="bg-slate-50 p-3 rounded-lg flex justify-between items-start">
                        <div>
                          <p className="font-medium">{emp.employer_name || emp.company_name || '-'}</p>
                          <p className="text-sm text-slate-600">{emp.designation || '-'}</p>
                        </div>
                        <Badge variant="outline">{emp.date_of_joining || emp.employed_from || emp.from_date || '-'} - {emp.date_of_relieving || emp.employed_to || emp.to_date || 'Present'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(employee?.education_history?.length > 0 || employee?.education_entries?.length > 0) && (
                <div className="mt-6 pt-6 border-t">
                  <p className="font-medium mb-3">Education History</p>
                  <div className="space-y-3">
                    {(employee.education_entries || employee.education_history || []).map((edu, idx) => (
                      <div key={idx} className="bg-slate-50 p-3 rounded-lg flex justify-between items-start">
                        <div>
                          <p className="font-medium">{edu.institution_name || edu.college || '-'}</p>
                          <p className="text-sm text-slate-600">{edu.course || edu.degree || '-'} - {edu.specialization || edu.field_of_study || '-'}</p>
                        </div>
                        <Badge variant="outline">{edu.year_of_passing || '-'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="w-5 h-5" />
                Case Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <InfoItem label="Case ID" value={caseData?.case_id || caseData?.id} mono />
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <Badge data-testid="case-detail-status-badge" className={getStatusBadgeClass(caseData?.status, caseData?.overall_result)}>
                  {caseData?.status?.replace(/_/g, ' ').toUpperCase()}
                </Badge>
              </div>
              <InfoItem label="Assigned To" value={caseData?.assigned_to || 'Unassigned'} />
              <InfoItem label="Created At" value={caseData?.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'N/A'} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Verification Checks</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {caseData?.checks?.map((check, idx) => {
                  const checkData = check.verified_details && Object.keys(check.verified_details).length > 0 
                    ? check.verified_details 
                    : check.subcheck_data || {};
                  const hasData = Object.keys(checkData).length > 0;
                  return (
                    <div key={idx} className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <p className="font-medium text-sm">{CHECK_NAMES[check.check_type] || CHECK_NAMES[check.name] || check.check_type || check.name}</p>
                          {check.check_id && <p className="text-xs text-slate-400 font-mono">{check.check_id}</p>}
                          {check.sub_option && <p className="text-xs text-slate-500 capitalize">{check.sub_option.replace(/_/g, ' ')}</p>}
                        </div>
                        <Badge className={getStatusBadgeClass(check.status)}>
                          {check.status?.replace(/_/g, ' ').toUpperCase() || 'PENDING'}
                        </Badge>
                      </div>
                      {hasData && (
                        <div className="mt-2 bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                          {Object.entries(checkData)
                            .filter(([k, v]) => v && !['verification_submitted','_id'].includes(k))
                            .slice(0, 8)
                            .map(([k, v]) => (
                              <div key={k} className="flex gap-2">
                                <span className="text-slate-500 capitalize min-w-[120px]">{k.replace(/_/g, ' ')}:</span>
                                <span className="text-slate-800 font-medium">{String(v)}</span>
                              </div>
                            ))}
                          {Object.keys(checkData).filter(k => checkData[k] && !['verification_submitted','_id'].includes(k)).length > 8 && (
                            <p className="text-slate-400 italic">+{Object.keys(checkData).filter(k => checkData[k]).length - 8} more fields</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {(!caseData?.checks || caseData.checks.length === 0) && (
                  <div className="p-4 text-center text-slate-500">No checks found</div>
                )}
              </div>
            </CardContent>
          </Card>

          <ReminderHistoryCard caseId={caseData?.id || caseData?.case_id} />
        </div>
      </div>
    </div>
  );
}

function ReminderHistoryCard({ caseId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !caseId || data) return;
    setLoading(true);
    axios.get(`/cases/${caseId}/reminder-history`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load reminder history'))
      .finally(() => setLoading(false));
  }, [open, caseId, data]);

  if (!caseId) return null;

  const summary = data?.summary || { total: 0, initial: 0, reminders: 0 };

  return (
    <Card className="mt-4" data-testid="reminder-history-card">
      <CardHeader
        className="pb-3 cursor-pointer hover:bg-slate-50 rounded-t-lg"
        onClick={() => setOpen((o) => !o)}
      >
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-600" />
            Reminder History
            {data && (
              <span className="ml-2 flex gap-1.5">
                <Badge variant="outline" className="text-[10px] bg-slate-50">
                  {summary.total} total
                </Badge>
                {summary.reminders > 0 && (
                  <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-700">
                    {summary.reminders} reminder{summary.reminders === 1 ? '' : 's'}
                  </Badge>
                )}
              </span>
            )}
          </span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Email chase trail for this case — HR / RM / university / vendor only. Candidate emails are excluded.
        </p>
      </CardHeader>
      {open && (
        <CardContent>
          {loading && <p className="text-sm text-slate-500 py-3">Loading…</p>}
          {!loading && data && data.history.length === 0 && (
            <p className="text-sm text-slate-500 py-3" data-testid="reminder-history-empty">
              No verification emails have been sent for this case yet.
            </p>
          )}
          {!loading && data && data.history.length > 0 && (
            <div className="space-y-2" data-testid="reminder-history-list">
              {data.history.map((row, i) => (
                <div
                  key={row.id || i}
                  className="border rounded-lg p-3 text-xs hover:bg-slate-50"
                  data-testid={`reminder-history-row-${i}`}
                >
                  <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          row.is_reminder
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                        }
                      >
                        {row.is_reminder ? 'Reminder' : 'Initial Send'}
                      </Badge>
                      {row.check_id && (
                        <span className="font-mono text-[10px] text-slate-500">{row.check_id}</span>
                      )}
                      {row.check_type && (
                        <span className="text-[10px] text-slate-500 capitalize">
                          {row.check_type.replace(/_/g, ' ')}
                        </span>
                      )}
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          row.status === 'sent' ? 'border-emerald-300 text-emerald-700' : 'border-rose-300 text-rose-700'
                        }`}
                      >
                        {row.status}
                      </Badge>
                    </div>
                    <span className="text-[10px] font-mono text-slate-500">
                      {(row.sent_at || '').replace('T', ' ').slice(0, 19)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 mt-1">
                    <div>
                      <span className="text-slate-500">To: </span>
                      <span className="font-medium">{row.recipient_email}</span>
                      {row.recipient_name && (
                        <span className="text-slate-500"> ({row.recipient_name})</span>
                      )}
                      {row.recipient_type && (
                        <span className="ml-1 text-[10px] uppercase text-slate-400">[{row.recipient_type}]</span>
                      )}
                    </div>
                    <div>
                      <span className="text-slate-500">From: </span>
                      <span>{row.sender_email}</span>
                      {row.sent_by_agent_name && (
                        <span className="text-slate-500"> · by {row.sent_by_agent_name}</span>
                      )}
                    </div>
                  </div>

                  {row.cc_emails && row.cc_emails.length > 0 && (
                    <div className="mt-1">
                      <span className="text-slate-500">CC: </span>
                      <span className="text-slate-700">{row.cc_emails.join(', ')}</span>
                    </div>
                  )}

                  {row.subject && (
                    <div className="mt-1 truncate">
                      <span className="text-slate-500">Subject: </span>
                      <span className="text-slate-700">{row.subject}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function InfoItem({ label, value, mono }) {
  return (
    <div>
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`font-medium ${mono ? 'font-mono text-sm' : ''}`}>{value || 'N/A'}</p>
    </div>
  );
}

function AddressCard({ title, address }) {
  return (
    <div className="bg-slate-50 p-3 rounded-lg">
      <p className="text-xs text-slate-500 uppercase mb-1">{title}</p>
      <p className="text-sm">{address.address_line1}</p>
      {address.address_line2 && <p className="text-sm">{address.address_line2}</p>}
      <p className="text-sm">{address.city}, {address.state} - {address.pincode}</p>
    </div>
  );
}

function DocCard({ label, value }) {
  return (
    <div className="bg-slate-50 p-3 rounded-lg">
      <p className="text-xs text-slate-500 uppercase mb-1">{label}</p>
      <p className="font-mono text-sm">{value}</p>
    </div>
  );
}
